import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Text,
  Badge,
  HStack,
  Spinner,
  Switch,
  Divider,
  FormErrorMessage,
  FormHelperText,
  SimpleGrid,
  Box,
  Textarea,
  Portal,
  keyframes,
} from '@chakra-ui/react'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuDownload,
  LuFileJson,
  LuFileSpreadsheet,
  LuFileText,
  LuHospital,
  LuImage,
} from 'react-icons/lu'
import { downloadJSON, downloadFHIR, downloadCSV, getPDFData } from '../../../../api/blot-export'
import { BlotInterpretation } from './BlotGallery'
import { useMyProfile, useMyProfileWithModules } from '../../../../api/api_hooks/user/myProfile'
import { useCHUConfig } from '../../../../contexts/CHUConfigContext'
import { isAIAccessible as isAIChuAccessible } from '../../../../config/chuConfig'
import { PlanType } from '../../../../types/subscription'
import type { ManualResult } from '../../../ManualResultSelector'
import jsPDF from 'jspdf'
import { pathologyDisplayName, isCompleteStripId } from '../utils/displayName'

interface ExportButtonProps {
  caseId: string
  sampleId?: string
  pathologyName?: string
  isDisabled?: boolean
  /** Raw source image preview URL used in the workspace */
  sourceImageDataUrl?: string | null
  blotInterpretations?: BlotInterpretation[]
  /** Recognised / edited strip identifier per blot — exports gate on all being complete. */
  blotStripIds?: Array<{ id: number; stripId: string | null }>
  /** Distinct reagent lots printed on the plate for the strips in export scope.
   *  Exactly one → the lot field is pre-filled from it. */
  scopedLots?: string[]
  /** Manual validation results per blot (non-premium mode) */
  manualBlotResults?: Record<number, ManualResult>
  /** Operator info to embed in PDF when config.export.include_operator is true */
  operatorName?: string | null
  operatorTimestamp?: string | null
  /** Case-level comment to embed in PDF when config.export.include_comments is true */
  caseComment?: string
  /** Notifies the parent when the user edits the comment from the PDF export modal. */
  onCaseCommentChange?: (value: string) => void
  /** Per-blot operator comments (from modal textarea) */
  blotComments?: Record<number, string>
  /** Per-blot placed bands with Y positions (from manual identification in modal) */
  placedBands?: Record<number, Array<{name: string; yFraction: number}>>
  /** Bordeaux/Dynablot multitest only: restricts every export format to these
   *  blot ids — the gallery's active pathology filter. Undefined/empty
   *  exports the whole case, as before. */
  includeBlotIds?: number[]
}

const loadImageAsPngDataUrl = (src: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new window.Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      try {
        const naturalW = image.naturalWidth || image.width
        const naturalH = image.naturalHeight || image.height
        // Cap canvas at 4000px on the longest side to avoid memory errors
        const MAX_PX = 4000
        const longestSide = Math.max(naturalW, naturalH)
        const scale = longestSide > MAX_PX ? MAX_PX / longestSide : 1
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(naturalW * scale)
        canvas.height = Math.round(naturalH * scale)
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas unavailable')); return }
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/png'))
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Image conversion failed'))
      }
    }
    image.onerror = () => reject(new Error('Image loading failed'))
    image.src = src
  })

// ── PDF image budget ────────────────────────────────────────────────────────
// The raw source scan is ~4960×6944 px. Embedded as PNG it produced ~36 MB
// reports, which hospital mail servers (often capped at 5–10 MB) reject outright.
// These settings bring a full report to ~1–2 MB while keeping the bands — including
// faint ones — clearly readable. Raise the quality/px if a site needs more detail.
const PDF_SOURCE_MAX_PX = 2200
const PDF_SOURCE_QUALITY = 0.85
const PDF_CROP_MAX_PX = 1400
const PDF_CROP_QUALITY = 0.92

/**
 * Load an image and return a *compressed JPEG* data URL, downscaled so its
 * longest side is at most `maxPx`. Used for every photographic image in the PDF
 * (source scan + blot crops); the logo stays PNG to keep its transparency.
 */
const loadImageAsJpegDataUrl = (src: string, maxPx: number, quality: number): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new window.Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      try {
        const naturalW = image.naturalWidth || image.width
        const naturalH = image.naturalHeight || image.height
        const longestSide = Math.max(naturalW, naturalH)
        const scale = longestSide > maxPx ? maxPx / longestSide : 1
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, Math.round(naturalW * scale))
        canvas.height = Math.max(1, Math.round(naturalH * scale))
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas unavailable')); return }
        // JPEG has no alpha channel — paint white first so any transparency
        // renders white instead of black.
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', quality))
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Image conversion failed'))
      }
    }
    image.onerror = () => reject(new Error('Image loading failed'))
    image.src = src
  })

const fitImageToBox = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
) => {
  const safeWidth = width > 0 ? width : maxWidth
  const safeHeight = height > 0 ? height : maxHeight
  const ratio = Math.min(maxWidth / safeWidth, maxHeight / safeHeight)

  return {
    width: safeWidth * ratio,
    height: safeHeight * ratio,
  }
}


/** Extracts band names from placed bands — handles both string[] (old) and {name,yFraction}[] (new) formats */
const getBandNames = (bands: any[]): string[] =>
  bands.map(b => (typeof b === 'string' ? b : b?.name)).filter(Boolean)

/**
 * Attention burst played once, when the case becomes exportable — i.e. every
 * strip validated AND every identifier complete (see `canExport` in the
 * workspace). The button was already unlocking silently at that moment, which
 * asked the reader to notice a disabled state had lifted.
 *
 * Animated through `filter`, deliberately: the workspace styles this button's
 * box-shadow, background and transition with `!important` to hold it in the FAB
 * grid, and an `!important` declaration beats an animation's value. `filter` is
 * untouched there, so the glow survives.
 */
const exportReadyPulse = keyframes`
  0%   { filter: drop-shadow(0 0 0 rgba(168, 85, 247, 0.55)); transform: scale(1); }
  35%  { filter: drop-shadow(0 0 14px rgba(168, 85, 247, 0.65)); transform: scale(1.09); }
  70%  { filter: drop-shadow(0 0 6px rgba(168, 85, 247, 0.35)); transform: scale(0.99); }
  100% { filter: drop-shadow(0 0 0 rgba(168, 85, 247, 0.55)); transform: scale(1); }
`

export default function ExportButton({
  caseId,
  sampleId,
  pathologyName,
  isDisabled = false,
  sourceImageDataUrl = null,
  blotInterpretations = [],
  blotStripIds = [],
  scopedLots = [],
  manualBlotResults = {},
  operatorName,
  operatorTimestamp,
  caseComment,
  onCaseCommentChange,
  blotComments = {},
  placedBands = {},
  includeBlotIds,
}: ExportButtonProps) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const chuConfig = useCHUConfig()
  const chuFeatures = chuConfig.features
  // Get user profile (for organization name)
  const { data: userProfile } = useMyProfile()
  // Read AI access from the USER PLAN (same gate the workspace uses) — not just
  // the CHU config. An admin / Premium / Enterprise user has AI verdicts in the
  // gallery and the PDF must use those same verdicts to stay consistent.
  const { data: currentUserModules } = useMyProfileWithModules()
  const userPlan = currentUserModules?.plan
  const isAdmin = currentUserModules?.role === 'admin' || currentUserModules?.role === 'super_admin'
  const aiAccessible = isAdmin
    || userPlan === PlanType.PREMIUM
    || userPlan === PlanType.ENTERPRISE
    || isAIChuAccessible(chuConfig)

  // Separate disclosures for PDF and FHIR modals
  const { isOpen: isPdfOpen, onOpen: onPdfOpen, onClose: onPdfClose } = useDisclosure()
  const { isOpen: isFhirOpen, onOpen: onFhirOpen, onClose: onFhirClose } = useDisclosure()

  const [exporting, setExporting] = useState<string | null>(null)
  // Plays once per unlock, then the button settles back: a permanent flash
  // would become furniture and stop signalling anything. Re-arms if the case
  // falls back to incomplete (a strip un-validated, an id cleared), so the
  // next completion is signalled again.
  const [justUnlocked, setJustUnlocked] = useState(false)
  // Dynablot prints the reagent lot of every strip in its table. Pre-fill the
  // field from it, but ONLY when the strips in scope agree on one lot: a report
  // mixes lots even within a single self-test (measured: Aspergillus IgG on
  // P08022-161-8 then P08022-162-5), and a single field cannot represent two.
  // An empty field the operator fills is a nuisance; a wrong lot on a validated
  // report is a traceability error.
  const wasDisabled = useRef(isDisabled)
  const mountedAt = useRef(Date.now())
  useEffect(() => {
    // Opening a case that was ALREADY finished also flips this flag, as the
    // analysis arrives after mount — that is a page load, not an
    // accomplishment, and celebrating it would make the pulse meaningless.
    // Only an unlock the reader caused, with the case already on screen.
    const settled = Date.now() - mountedAt.current > 1500
    if (wasDisabled.current && !isDisabled && settled) setJustUnlocked(true)
    wasDisabled.current = isDisabled
  }, [isDisabled])
  useEffect(() => {
    if (!justUnlocked) return undefined
    const timer = setTimeout(() => setJustUnlocked(false), 4400)
    return () => clearTimeout(timer)
  }, [justUnlocked])
  const [lotNumberTouched, setLotNumberTouched] = useState(false)
  const [transferLotNumberTouched, setTransferLotNumberTouched] = useState(false)

  // PDF options — seeded from CHU config
  const [pdfOptions, setPdfOptions] = useState({
    lotNumber: '',
    transferLotNumber: '',
    realizationDate: new Date().toISOString().slice(0, 10),
    organizationName: '',
    includeBlotImages: chuFeatures.export.include_raw_images,
    includeComments: chuFeatures.export.include_comments,
    includeOperator: chuFeatures.export.include_operator,
  })

  // FHIR options (sans patient info)
  const [fhirOptions, setFhirOptions] = useState({
    organizationName: '',
  })

  // Initialize organization from user profile
  useEffect(() => {
    if (userProfile?.companyName) {
      setPdfOptions(prev => ({ ...prev, organizationName: userProfile.companyName }))
      setFhirOptions(prev => ({ ...prev, organizationName: userProfile.companyName }))
    }
  }, [userProfile?.companyName])

  // Validation for lot numbers
  const lotFromPlate = scopedLots.length === 1 ? scopedLots[0] : null
  // DERIVED, never stored. Writing the plate's lot into the modal's state made
  // it outlive the scope it came from: changing the pathology filter left the
  // previous pathology's lot in the field, and the CSV/JSON/FHIR exports — which
  // open no modal at all — carried that frozen value whatever the filter said.
  // Here the operator's own entry wins when there is one, and otherwise the
  // value follows the scope at every render, for every export path.
  const effectiveLotNumber = pdfOptions.lotNumber.trim() || lotFromPlate || ''

  const isLotNumberInvalid = lotNumberTouched && !effectiveLotNumber.trim()
  const isTransferLotNumberInvalid = transferLotNumberTouched && !pdfOptions.transferLotNumber.trim()
  // Every strip must carry a complete identifier (letter + number) before any
  // export: a report that ships "Blot 3" or a half-read "K" can't be traced back
  // to a patient sample. List the still-incomplete strips so the biologist knows
  // exactly which ones to fill in.
  const missingStripLabels = blotStripIds
    .filter((b) => !isCompleteStripId(b.stripId))
    .map((b) => `Blot ${b.id}`)
  const allStripIdsComplete = missingStripLabels.length === 0

  const canExportPDF =
    allStripIdsComplete &&
    effectiveLotNumber.trim().length > 0 &&
    pdfOptions.transferLotNumber.trim().length > 0 &&
    !!pdfOptions.realizationDate

  // Reset validation state when modal closes
  const handlePdfClose = () => {
    setLotNumberTouched(false)
    setTransferLotNumberTouched(false)
    onPdfClose()
  }

  // Builds the ExportContext payload from the workspace state — the same shape
  // is sent to JSON/CSV/FHIR so the three formats now carry the operator's
  // verdicts, comments, manually-placed bands, lot numbers and organization.
  const buildExportContext = (): import('../../../../api/blot-export').ExportContext => ({
    operator: (operatorName || operatorTimestamp)
      ? { name: operatorName, timestamp: operatorTimestamp }
      : undefined,
    verdicts: Object.fromEntries(
      Object.entries(manualBlotResults).filter(([, v]) => v != null)
    ) as any,
    caseComment: caseComment || undefined,
    blotComments,
    placedBands,
    // Detected ∪ manual band names per blot — lets JSON/CSV/FHIR list the
    // detected bands too, not just the manually-added ones.
    detectedBands: Object.fromEntries(
      (blotInterpretations || [])
        .filter((i) => (i.identifiedBandNames?.length ?? 0) > 0)
        .map((i) => [i.blotId, i.identifiedBandNames])
    ),
    // Paired-comparison roles (CIP-WB mother/child), flips included. The server
    // cannot derive them: the pairing is guessed from the strip row order in the
    // gallery and the reader may have corrected it.
    pairedRoles: Object.fromEntries(
      (blotInterpretations || [])
        .flatMap((i) => (i.pairedRole ? [[i.blotId, i.pairedRole] as const] : []))
    ),
    lots: (effectiveLotNumber || pdfOptions.transferLotNumber || pdfOptions.realizationDate)
      ? {
          lotNumber: effectiveLotNumber || undefined,
          transferLotNumber: pdfOptions.transferLotNumber || undefined,
          realizationDate: pdfOptions.realizationDate || undefined,
        }
      : undefined,
    organizationName: pdfOptions.organizationName || fhirOptions.organizationName || undefined,
    includeBlotIds: includeBlotIds && includeBlotIds.length > 0 ? includeBlotIds : undefined,
  })

  // Defense-in-depth: the menu items are disabled while any id is missing, but
  // guard the handlers too so no export path can slip through.
  const blockIfIncomplete = (): boolean => {
    if (allStripIdsComplete) return false
    toast({
      title: t('blotAnalysis.export.missingIdsTitle', 'Identifiants incomplets'),
      description: t('blotAnalysis.export.missingIdsDesc', {
        defaultValue: 'Complétez l\'identifiant de : {{list}}',
        list: missingStripLabels.join(', '),
      }),
      status: 'warning',
      duration: 6000,
    })
    return true
  }

  const handleExportJSON = async () => {
    if (blockIfIncomplete()) return
    setExporting('json')
    try {
      await downloadJSON(caseId, sampleId, buildExportContext())
      toast({
        title: t('blotAnalysis.export.success', 'Export réussi'),
        description: t('blotAnalysis.export.jsonDownloaded', 'Fichier JSON téléchargé'),
        status: 'success',
        duration: 3000,
      })
    } catch (error) {
      toast({
        title: t('blotAnalysis.export.error', 'Erreur d\'export'),
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setExporting(null)
    }
  }

  const handleExportCSV = async () => {
    if (blockIfIncomplete()) return
    setExporting('csv')
    try {
      await downloadCSV(caseId, sampleId, buildExportContext())
      toast({
        title: t('blotAnalysis.export.success', 'Export réussi'),
        description: t('blotAnalysis.export.csvDownloaded', 'Fichier CSV téléchargé'),
        status: 'success',
        duration: 3000,
      })
    } catch (error) {
      toast({
        title: t('blotAnalysis.export.error', 'Erreur d\'export'),
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setExporting(null)
    }
  }

  const handleExportFHIR = async () => {
    if (blockIfIncomplete()) return
    setExporting('fhir')
    try {
      await downloadFHIR(caseId, sampleId, {
        ...buildExportContext(),
        organizationName: fhirOptions.organizationName,
      })
      toast({
        title: t('blotAnalysis.export.success', 'Export réussi'),
        description: t('blotAnalysis.export.fhirDownloaded', 'Rapport FHIR téléchargé'),
        status: 'success',
        duration: 3000,
      })
      onFhirClose()
    } catch (error) {
      toast({
        title: t('blotAnalysis.export.error', 'Erreur d\'export'),
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setExporting(null)
    }
  }

  const handleExportPDF = async () => {
    if (blockIfIncomplete()) return
    setExporting('pdf')
    try {
      const response = await getPDFData(caseId, {
        organizationName: pdfOptions.organizationName,
        includeImages: pdfOptions.includeBlotImages,
        includeBlotIds: includeBlotIds && includeBlotIds.length > 0 ? includeBlotIds : undefined,
      })

      const { pdfContent } = response.data
      const resolvedOperatorName =
        operatorName?.trim() ||
        userProfile?.contactName?.trim() ||
        userProfile?.email?.trim() ||
        null
      const resolvedOperatorTimestamp = operatorTimestamp || null
      const operatorTimestampLabel = operatorTimestamp ? 'Date de validation' : "Date d'export"
      let rawSourceImageForPdf: string | null = null
      let rawSourceImageUnavailable = false

      if (pdfOptions.includeBlotImages && sourceImageDataUrl) {
        try {
          rawSourceImageForPdf = await loadImageAsJpegDataUrl(
            sourceImageDataUrl, PDF_SOURCE_MAX_PX, PDF_SOURCE_QUALITY,
          )
        } catch (_e) {
          rawSourceImageUnavailable = true
        }
      }

      // Try to load the Careflai logo for the header / footer branding.
      // Resolves to null silently if unavailable so PDF still generates.
      let careflaiLogoDataUrl: string | null = null
      try {
        careflaiLogoDataUrl = await loadImageAsPngDataUrl('/logo.png')
      } catch (_e) {
        careflaiLogoDataUrl = null
      }

      // Generate PDF using jsPDF
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      let y = 20

      // Helper function to check and add new page if needed
      const checkNewPage = (requiredSpace: number = 30) => {
        if (y > pageHeight - requiredSpace) {
          doc.addPage()
          y = 38  // leave room for the brand band + logo (logo bottom ≈ y=30)
        }
      }

      // ── Brand header band (drawn on every page later via finalize loop) ─────
      // Teal/cyan band across the top + Careflai logo top-left.
      const drawBrandBand = () => {
        doc.setFillColor(13, 148, 136)  // teal.500
        doc.rect(0, 0, pageWidth, 12, 'F')
        // Subtle accent line just under the band
        doc.setFillColor(94, 234, 212)  // teal.200
        doc.rect(0, 12, pageWidth, 0.8, 'F')
        // Logo (top-left of the band area, below the band)
        if (careflaiLogoDataUrl) {
          try {
            // Use a square ~14mm box so the logo fits regardless of aspect
            doc.addImage(careflaiLogoDataUrl, 'PNG', margin, 16, 14, 14)
          } catch (_e) {
            // ignore image errors silently
          }
        }
        // Brand name to the right of the logo
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(13, 148, 136)
        doc.text('Careflai', margin + (careflaiLogoDataUrl ? 18 : 0), 26)
        doc.setTextColor(0)
      }

      // Reserve room below the band on page 1
      drawBrandBand()
      y = 38

      // Title block (centered)
      doc.setFontSize(17)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(15, 23, 42)  // slate.900
      doc.text(pdfContent.header.title, pageWidth / 2, y, { align: 'center' })
      y += 7

      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(71, 85, 105)  // slate.600
      doc.text(pdfContent.header.subtitle, pageWidth / 2, y, { align: 'center' })
      y += 5

      doc.setFontSize(9)
      doc.setTextColor(100, 116, 139)  // slate.500
      doc.text(pdfContent.header.date, pageWidth / 2, y, { align: 'center' })
      doc.setTextColor(0)
      y += 12

      // Identification section with a colored side bar
      doc.setFillColor(13, 148, 136)
      doc.rect(margin - 2, y - 4, 1.5, 7, 'F')
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(15, 23, 42)
      doc.text('Identification', margin + 2, y)
      y += 7

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0)
      doc.text(`N° d'échantillon: ${pdfContent.identification.sampleId}`, margin, y)
      y += 5
      doc.text(`Fichier: ${pdfContent.identification.filename}`, margin, y)
      y += 5
      doc.text(t('blotAnalysis.export.analysisDate', { date: pdfContent.identification.analysisDate }), margin, y)
      y += 5
      doc.text(t('blotAnalysis.export.organizationLine', { name: pdfOptions.organizationName }), margin, y)
      y += 5

      // Realization date (required) — different from export date
      if (pdfOptions.realizationDate) {
        const realizationDateFr = new Date(pdfOptions.realizationDate + 'T00:00:00').toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        })
        doc.text(t('blotAnalysis.export.realizationDateLine', { date: realizationDateFr }), margin, y)
        y += 5
      }

      // Reagent lot number (required)
      if (effectiveLotNumber) {
        doc.text(t('blotAnalysis.export.lotNumberLine', { lot: effectiveLotNumber }), margin, y)
        y += 5
      }

      // Transfer lot number (optional)
      if (pdfOptions.transferLotNumber.trim()) {
        doc.text(t('blotAnalysis.export.transferLotNumberLine', { lot: pdfOptions.transferLotNumber.trim() }), margin, y)
        y += 5
      }

      // Operator tracking — only if enabled in config and data available
      if (pdfOptions.includeOperator && (resolvedOperatorName || resolvedOperatorTimestamp)) {
        if (resolvedOperatorName) {
          doc.text(t('blotAnalysis.export.operator', { name: resolvedOperatorName }), margin, y)
          y += 5
        }
        if (resolvedOperatorTimestamp) {
          const ts = new Date(resolvedOperatorTimestamp).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
          })
          doc.text(`${operatorTimestampLabel} : ${ts}`, margin, y)
          y += 5
        }
      }
      y += 7

      // Test Info
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(t('blotAnalysis.export.testInfo'), margin, y)
      y += 7

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      if (pdfContent.testInfo.name) {
        doc.text(`Test: ${pdfContent.testInfo.name}`, margin, y)
        y += 5
      }
      doc.text(`Fabricant: ${pdfContent.testInfo.manufacturer}`, margin, y)
      y += 5
      if (pdfContent.testInfo.version) {
        doc.text(`Version: ${pdfContent.testInfo.version}`, margin, y)
        y += 5
      }
      if (pdfContent.testInfo.antibodyClass) {
        doc.text(`Classe d'anticorps: ${pdfContent.testInfo.antibodyClass}`, margin, y)
        y += 5
      }
      y += 7

      // Summary
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(t('blotAnalysis.export.resultsSummary'), margin, y)
      y += 7

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      // summary.totalBlots is the stored column, which the backend never
      // recomputes after validation and can read 0. The report already carries
      // the real per-blot list, so count that instead (fallback to the stored
      // value only if the list is missing).
      doc.text(t('blotAnalysis.export.blotsAnalysed', { count: Array.isArray(pdfContent.blotResults) ? pdfContent.blotResults.length : pdfContent.summary.totalBlots }), margin, y)
      y += 5
      doc.text(t('blotAnalysis.export.bandsDetected', { count: pdfContent.summary.totalBands }), margin, y)
      y += 5
      doc.text(t('blotAnalysis.export.positiveControls', { count: pdfContent.summary.positiveControlCount }), margin, y)
      y += 5
      doc.text(t('blotAnalysis.export.processingTime', { time: pdfContent.summary.processingTime }), margin, y)
      y += 12

      // Raw source image — full dedicated page
      if (pdfOptions.includeBlotImages && rawSourceImageForPdf) {
        doc.addPage()
        const srcPageW = doc.internal.pageSize.getWidth()
        const srcPageH = doc.internal.pageSize.getHeight()
        const srcMargin = 12
        // Leave room for the brand band (will be drawn in the finalize loop).
        const topReserved = 34
        const labelY = topReserved + 4
        const imageY = labelY + 6

        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(80)
        doc.text('Image source brute', srcMargin, labelY)
        doc.setTextColor(0)

        try {
          const imageProps = doc.getImageProperties(rawSourceImageForPdf)
          const availW = srcPageW - srcMargin * 2
          const availH = srcPageH - imageY - 22  // reserve footer space too
          const fitted = fitImageToBox(imageProps.width, imageProps.height, availW, availH)
          const imgX = srcMargin + (availW - fitted.width) / 2
          doc.addImage(rawSourceImageForPdf, 'JPEG', imgX, imageY, fitted.width, fitted.height)
        } catch (_e) {
          rawSourceImageUnavailable = true
          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100)
          doc.text(t('blotAnalysis.export.rawPreviewNA'), srcMargin, imageY + 8)
          doc.setTextColor(0)
        }

        // Back to content page
        doc.addPage()
        y = 38
      } else if (pdfOptions.includeBlotImages && rawSourceImageUnavailable) {
        checkNewPage(20)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100)
        doc.text(t('blotAnalysis.export.rawPreviewNAPdf'), margin, y)
        doc.setTextColor(0)
        y += 8
      }

      // Performance if available
      if (pdfContent.performance) {
        checkNewPage(40)
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text(t('blotAnalysis.export.performance'), margin, y)
        y += 7

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.text(
          t('blotAnalysis.export.sensitivityLine', { value: pdfContent.performance.sensitivity, ci: pdfContent.performance.sensitivityCI || 'N/A' }),
          margin,
          y
        )
        y += 5
        doc.text(
          t('blotAnalysis.export.specificityLine', { value: pdfContent.performance.specificity, ci: pdfContent.performance.specificityCI || 'N/A' }),
          margin,
          y
        )
        y += 12
      }

      // Interpretation criteria if available
      // Toujours inclus : les critères du kit sont ce qui rend un verdict
      // relisible par un tiers.
      if (pdfContent.interpretation) {
        checkNewPage(60)

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text(t('blotAnalysis.export.criteria'), margin, y)
        y += 7

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')

        // Positive
        doc.setTextColor(0, 128, 0)
        doc.text('POSITIF:', margin, y)
        doc.setTextColor(0)
        y += 5
        pdfContent.interpretation.positive.criteria.forEach((criteria) => {
          checkNewPage(10)
          doc.text(`  • ${criteria}`, margin, y)
          y += 5
        })
        y += 3

        // Negative
        doc.setTextColor(200, 0, 0)
        doc.text(t('blotAnalysis.export.negativeLabel'), margin, y)
        doc.setTextColor(0)
        y += 5
        pdfContent.interpretation.negative.criteria.forEach((criteria) => {
          checkNewPage(10)
          doc.text(`  • ${criteria}`, margin, y)
          y += 5
        })
        y += 3
        y += 7
      }

      // Blot Results with optional images
      if (pdfContent.blotResults.length > 0) {
        checkNewPage(50)

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text(t('blotAnalysis.export.blotDetails'), margin, y)
        y += 10

        // Legend — explains the band-name colours (text) AND the coloured ticks on
        // the strip images, which now share one code. Shown whenever any band is
        // rendered, so the violet "détectée" colour is always documented; the manual
        // entry is only meaningful once a band was actually added by hand.
        const anyManualBand = blotInterpretations.some((i) => (i.bandLines ?? []).some((l) => l.manual))
        const anyBand = blotInterpretations.some((i) => (i.bandLines ?? []).length > 0)
        if (anyBand) {
          doc.setFontSize(8)
          doc.setFont('helvetica', 'normal')
          const ly = y
          doc.setTextColor(0)
          doc.text(t('blotAnalysis.export.bandLegend'), margin, ly)
          let lx = margin + doc.getTextWidth(t('blotAnalysis.export.bandLegend') + ' ') + 1
          doc.setFillColor(128, 90, 213)
          doc.circle(lx, ly - 1, 1, 'F')
          doc.setTextColor(128, 90, 213)
          doc.text(t('blotAnalysis.export.detected'), lx + 3, ly)
          lx += 3 + doc.getTextWidth(t('blotAnalysis.export.detected')) + 6
          if (anyManualBand) {
            doc.setFillColor(0, 80, 180)
            doc.circle(lx, ly - 1, 1, 'F')
            doc.setTextColor(0, 80, 180)
            doc.text(t('blotAnalysis.export.manuallyAdded'), lx + 3, ly)
            doc.setTextColor(0)
          }
          y += 5
          // Second line — same colour code applies to BOTH the band text above and
          // the ticks drawn on the strip images.
          doc.setFontSize(7)
          doc.setTextColor(90)
          doc.text(
            t('blotAnalysis.export.legendLine2'),
            margin,
            y + 1,
          )
          doc.setTextColor(0)
          y += 6
        }

        // If including images, show each blot with its image
        if (pdfOptions.includeBlotImages) {
          const maxImageWidth = 45
          const maxImageHeight = 70
          const colWidth = (pageWidth - margin * 2) / 3

          // Pre-compress the strip crops: the render loop below is synchronous so
          // it can't await. Embedding the raw PNGs is a large share of the report
          // weight; JPEG at PDF_CROP_QUALITY keeps the bands readable. A crop that
          // fails to compress simply falls back to its original PNG.
          const compressedCrops = new Map<number, string>()
          await Promise.all(
            pdfContent.blotResults.map(async (blot: any, index: number) => {
              if (!blot.cropImage) return
              const src = blot.cropImage.startsWith('data:')
                ? blot.cropImage
                : `data:image/png;base64,${blot.cropImage}`
              try {
                compressedCrops.set(
                  index,
                  await loadImageAsJpegDataUrl(src, PDF_CROP_MAX_PX, PDF_CROP_QUALITY),
                )
              } catch {
                /* keep the original PNG for this crop */
              }
            }),
          )

          let rowExtra = 0
          pdfContent.blotResults.forEach((blot, index) => {
            // Calculate proper image dimensions maintaining aspect ratio
            let imageWidth = maxImageWidth
            let imageHeight = maxImageHeight

            // Use actual crop dimensions if available for proper aspect ratio
            if (blot.cropWidth && blot.cropHeight) {
              const aspectRatio = blot.cropWidth / blot.cropHeight
              if (aspectRatio > maxImageWidth / maxImageHeight) {
                // Image is wider than space - constrain by width
                imageWidth = maxImageWidth
                imageHeight = maxImageWidth / aspectRatio
              } else {
                // Image is taller than space - constrain by height
                imageHeight = maxImageHeight
                imageWidth = maxImageHeight * aspectRatio
              }
            }

            // Check if we need a new page (image + text needs ~100px)
            if (y > pageHeight - 110) {
              doc.addPage()
              y = 38
            }

            const xPos = margin + (index % 3) * colWidth

            // Add image if available
            if (blot.cropImage) {
              try {
                const compressedCrop = compressedCrops.get(index)
                const imgData = compressedCrop ?? (blot.cropImage.startsWith('data:')
                  ? blot.cropImage
                  : `data:image/png;base64,${blot.cropImage}`)
                doc.addImage(imgData, compressedCrop ? 'JPEG' : 'PNG', xPos, y, imageWidth, imageHeight)

                // Lines to draw over the image:
                // - Manual mode: user-placed bands (yFraction from click position)
                // - AI mode: AI-identified bands from the gallery interpretation
                //   (yFraction derived from sample profile position)
                const galleryInterpForLines = blotInterpretations.find(i => i.blotId === blot.id)
                // bandLines carries BOTH detected and manually-added bands (each
                // with a `manual` flag) and stays in sync with the interpretation,
                // so manual lines show even when the export placedBands record lags
                // (e.g. right after a page reload). Non-AI mode → manual lines only.
                const bl = galleryInterpForLines?.bandLines ?? []
                let linesToDraw = bl
                  .filter((l) => l?.name && (aiAccessible || l.manual))
                  .map((l) => ({ name: l.name, yFraction: l.yFraction, manual: !!l.manual }))
                // Fallback if the interpretation has no lines yet.
                if (linesToDraw.length === 0) {
                  linesToDraw = (placedBands[blot.id] || [])
                    .map((entry: any) => ({
                      name: typeof entry === 'string' ? entry : entry?.name,
                      yFraction: typeof entry === 'string' ? 0.5 : (entry?.yFraction ?? 0.5),
                      manual: true,
                    }))
                    .filter((l: any) => l.name)
                }
                // Match the on-screen markers: a SHORT tick just beside the
                // strip's right edge (not a full-width line across the band) +
                // the name, coloured like the profile — green for the témoin
                // (positive control), purple for the bandelette (sample).
                const [cr, cg, cb] = blot.isPositiveControl ? [34, 197, 94] : [128, 90, 213]
                const tickLen = 2.5 // mm, short tick outside the strip
                // Distribute labels LEFT/RIGHT of the strip so close bands don't
                // overlap — mirrors the on-screen assignLabelSides (greedy over
                // height, RIGHT preferred, LEFT when the right is still occupied).
                // minGap ≈ the 5pt label height in mm.
                const minGap = 2.2
                const lastTop = { left: -Infinity, right: -Infinity }
                const sidedLines = [...linesToDraw]
                  .sort((a, b) => a.yFraction - b.yFraction)
                  .map((l) => {
                    const top = l.yFraction * imageHeight
                    const rightFree = top - lastTop.right >= minGap
                    const leftFree = top - lastTop.left >= minGap
                    let side: 'left' | 'right'
                    if (rightFree) side = 'right'
                    else if (leftFree) side = 'left'
                    else side = lastTop.right <= lastTop.left ? 'right' : 'left'
                    lastTop[side] = top
                    return { ...l, side }
                  })
                sidedLines.forEach(({ name, yFraction, manual, side }) => {
                  // Manually-added bands in blue to match the text legend.
                  const [lr, lg, lb] = manual ? [0, 80, 180] : [cr, cg, cb]
                  const lineY = y + yFraction * imageHeight
                  doc.setDrawColor(lr, lg, lb)
                  doc.setLineWidth(0.5)
                  doc.setFontSize(5)
                  doc.setTextColor(lr, lg, lb)
                  if (side === 'left') {
                    doc.line(xPos - tickLen, lineY, xPos, lineY)
                    doc.text(name, xPos - tickLen - 0.6, lineY + 1, { align: 'right' })
                  } else {
                    doc.line(xPos + imageWidth, lineY, xPos + imageWidth + tickLen, lineY)
                    doc.text(name, xPos + imageWidth + tickLen + 0.6, lineY + 1)
                  }
                  doc.setTextColor(0)
                  doc.setDrawColor(0)
                })
              } catch (e) {
                // Image failed to load, show placeholder
                doc.setDrawColor(200)
                doc.rect(xPos, y, maxImageWidth, maxImageHeight)
                doc.setFontSize(8)
                doc.text('Image N/A', xPos + 10, y + 35)
              }
            }

            // Add blot info below image
            doc.setFontSize(9)
            doc.setFont('helvetica', 'bold')
            const stripName = (blot as any).stripId || `Blot ${blot.id}`
            const label = blot.isPositiveControl ? `${stripName} (Ctrl+)` : stripName
            doc.text(label, xPos, y + maxImageHeight + 5)
            // Dynablot: the sample reference — which PATIENT this strip is. The
            // strip id alone only names its slot in the cassette, so a report
            // without this cannot be filed against a patient.
            //
            // It takes a line of its own between the identifier and the band
            // list, and pushes everything below down by `infoShift`: the block's
            // vertical positions are fixed offsets, so printing here without
            // shifting them laid the reference straight over the band list
            // ("C+2M-012" on top of "5 bandes: P40, P38").
            const sampleRef = (blot as any).sampleLabel
            const infoShift = sampleRef ? 4 : 0
            if (sampleRef) {
              doc.setFontSize(7)
              doc.setTextColor(90)
              doc.text(String(sampleRef), xPos, y + maxImageHeight + 9)
              doc.setTextColor(0)
              doc.setFontSize(8)
            }

            // Interpretation: manual result takes priority in non-premium mode
            const manualResult = manualBlotResults[blot.id]
            const galleryInterp = blotInterpretations.find(i => i.blotId === blot.id)
            const bandNames = galleryInterp?.identifiedBandNames || []
            // Le rapport porte toujours un verdict, et dit d'où il vient : un
            // verdict validé par le biologiste est imprimé tel quel, celui
            // d'IRIS sur une bandelette non validée est suivi de la mention
            // « non validé » et n'est jamais confondu avec une validation.
            // L'export n'exige pas que tout soit validé (voir canExportPDF),
            // d'où la mention plutôt qu'un simple affichage.
            const interpretResult = manualResult
              || (!blot.isPositiveControl && aiAccessible && galleryInterp?.result
                ? `${galleryInterp.result} ${t('blotAnalysis.export.unvalidatedSuffix')}`
                : null)
              || blot.interpretation
              || 'À DÉTERMINER'

            doc.setFont('helvetica', 'normal')
            doc.setFontSize(8)

            // Bands = detected ∪ manual (identifiedBandNames already merges both).
            // The manual set comes SOLELY from the interpretation (bandLines.manual),
            // which reflects a band's ORIGIN. We deliberately do NOT treat mere
            // presence in placedBands as "manual": an auto-detected band that was
            // repositioned lives in placedBands too but must stay a detected band.
            const blotPlaced = placedBands[blot.id]
            const manualNamesSet = new Set<string>(
              (galleryInterp?.bandLines ?? []).filter((l) => l.manual).map((l) => l.name),
            )
            const allBandNames = bandNames.length > 0 ? bandNames : getBandNames(blotPlaced || [])
            if (allBandNames.length > 0) {
              const bandsY = y + maxImageHeight + 10 + infoShift
              let bx = xPos
              doc.setTextColor(0)
              const prefix = `${allBandNames.length} bandes: `
              doc.text(prefix, bx, bandsY)
              bx += doc.getTextWidth(prefix)
              // Text colours mirror the strip-image ticks exactly (same code for every
              // test type): blue = manually added · green = témoin (control) · violet =
              // détectée (auto sample). Applies to all immunoblot configs.
              allBandNames.forEach((name, i) => {
                const token = i < allBandNames.length - 1 ? `${name}, ` : name
                if (manualNamesSet.has(name)) doc.setTextColor(0, 80, 180)        // blue = manual
                else if (blot.isPositiveControl) doc.setTextColor(34, 197, 94)    // green = control
                else doc.setTextColor(128, 90, 213)                              // violet = detected
                doc.text(token, bx, bandsY)
                bx += doc.getTextWidth(token)
              })
              doc.setTextColor(0)
            }

            doc.setFontSize(8)
            doc.setFont('helvetica', 'bold')

            if (interpretResult.includes('POSITIF')) {
              doc.setTextColor(200, 0, 0)
            } else if (interpretResult.includes('NÉGATIF')) {
              doc.setTextColor(0, 128, 0)
            } else if (interpretResult.includes('DOUTEUX')) {
              doc.setTextColor(200, 150, 0)
            } else if (interpretResult === 'CONTRÔLE') {
              doc.setTextColor(0, 100, 200)
            } else {
              doc.setTextColor(100, 100, 100)
            }
            doc.text(interpretResult, xPos, y + maxImageHeight + 15 + infoShift)
            doc.setTextColor(0)

            // Borderline flag — nearly met the criteria; hint to confirm. Suppressed
            // once the biologist has validated the strip: a manual verdict IS the
            // confirmation, so keeping "/ a verifier" next to it is stale and reads as
            // a doubled validation state (mirrors the on-screen card gating).
            if (galleryInterp?.needsReview && manualResult == null) {
              const vw = doc.getTextWidth(interpretResult)
              doc.setFont('helvetica', 'bold')
              doc.setFontSize(7)
              doc.setTextColor(230, 145, 0)
              // Même libellé que le badge à l'écran — un seul terme pour un seul
              // état, quel que soit le sens du verdict.
              const flagTxt = '/ a verifier'
              doc.text(flagTxt, xPos + vw + 2, y + maxImageHeight + 15 + infoShift)
              doc.setTextColor(0)
              doc.setFontSize(8)
            }

            // Echinococcus species diagnosis (P1-P5 · espèce), when available
            const speciesLabel = galleryInterp?.speciesLabel
            if (speciesLabel) {
              doc.setFont('helvetica', 'bolditalic')
              doc.setFontSize(7.5)
              doc.setTextColor(128, 90, 213) // purple, like the on-screen card
              doc.text(speciesLabel, xPos, y + maxImageHeight + 19.5 + infoShift)
              doc.setTextColor(0)
              doc.setFont('helvetica', 'normal')
            }

            // Cotation semi-quantitative (Oliva 2015) — portée par les seuls cas
            // dont le profil l'active (Bordeaux) ; même ligne que le diagnostic
            // d'espèce, les deux ne coexistent jamais (pathologies différentes).
            const semiQuantLabel = (galleryInterp as any)?.semiQuantLabel
            if (semiQuantLabel) {
              doc.setFont('helvetica', 'bold')
              doc.setFontSize(7.5)
              doc.setTextColor(128, 90, 213)
              doc.text(semiQuantLabel, xPos, y + maxImageHeight + 19.5 + infoShift)
              doc.setTextColor(0)
              doc.setFont('helvetica', 'normal')
            }

            // Per-blot comment
            const blotComment = blotComments[blot.id]
            if (blotComment?.trim()) {
              doc.setFont('helvetica', 'italic')
              doc.setFontSize(7)
              doc.setTextColor(80)
              const commentBaseY = (speciesLabel || semiQuantLabel) ? 24 : 20
              const commentLines = doc.splitTextToSize(`Note: ${blotComment.trim()}`, colWidth - 4)
              commentLines.slice(0, 2).forEach((line: string, li: number) => {
                doc.text(line, xPos, y + maxImageHeight + commentBaseY + infoShift + li * 4)
              })
              doc.setTextColor(0)
            }

            // Move to next row after 3 blots. The row grows with the tallest
            // info block it contains — a Dynablot row carries one line more.
            rowExtra = Math.max(rowExtra, infoShift)
            if ((index + 1) % 3 === 0) {
              y += maxImageHeight + 30 + rowExtra
              rowExtra = 0
            }
          })

          // Ensure y is updated if last row wasn't complete
          if (pdfContent.blotResults.length % 3 !== 0) {
            y += maxImageHeight + 30 + rowExtra
            rowExtra = 0
          }
        } else {
          // Simple table without images
          doc.setFontSize(9)
          doc.setFont('helvetica', 'bold')
          doc.text('ID', margin, y)
          doc.text('Bandes', margin + 15, y)
          doc.text('Résultat', margin + 75, y)
          doc.text('Commentaire', margin + 110, y)
          y += 6

          doc.setFont('helvetica', 'normal')
          pdfContent.blotResults.forEach((blot) => {
            checkNewPage(12)

            const manualResult = manualBlotResults[blot.id]
            const galleryInterp = blotInterpretations.find(i => i.blotId === blot.id)
            const bandNames = galleryInterp?.identifiedBandNames || []
            // Le rapport porte toujours un verdict, et dit d'où il vient : un
            // verdict validé par le biologiste est imprimé tel quel, celui
            // d'IRIS sur une bandelette non validée est suivi de la mention
            // « non validé » et n'est jamais confondu avec une validation.
            // L'export n'exige pas que tout soit validé (voir canExportPDF),
            // d'où la mention plutôt qu'un simple affichage.
            const interpretResult = manualResult
              || (!blot.isPositiveControl && aiAccessible && galleryInterp?.result
                ? `${galleryInterp.result} ${t('blotAnalysis.export.unvalidatedSuffix')}`
                : null)
              || blot.interpretation
              || 'À DÉTERMINER'

            const rowSample = (blot as any).sampleLabel
            doc.text(
              rowSample
                ? `${(blot as any).stripId || `Blot ${blot.id}`} · ${rowSample}`
                : (blot as any).stripId || `Blot ${blot.id}`,
              margin,
              y,
            )

            // All bands = detected ∪ manual (identifiedBandNames already merges
            // both), so detected bands are never dropped when a band is added.
            const blotPlaced = placedBands[blot.id]
            const allBandNames = bandNames.length > 0 ? bandNames : getBandNames(blotPlaced || [])
            const bandsText = allBandNames.length > 0 ? `${allBandNames.length}: ${allBandNames.join(', ')}` : ''
            doc.text(bandsText, margin + 15, y)

            if (interpretResult.includes('POSITIF')) {
              doc.setTextColor(200, 0, 0)
            } else if (interpretResult.includes('NÉGATIF')) {
              doc.setTextColor(0, 128, 0)
            } else if (interpretResult.includes('DOUTEUX')) {
              doc.setTextColor(200, 150, 0)
            } else if (interpretResult === 'CONTRÔLE') {
              doc.setTextColor(0, 100, 200)
            } else {
              doc.setTextColor(100, 100, 100)
            }
            doc.text(interpretResult, margin + 85, y)
            doc.setTextColor(0)

            // Per-blot comment in text table
            const blotComment = blotComments[blot.id]
            if (blotComment?.trim()) {
              doc.setFontSize(7)
              doc.setFont('helvetica', 'italic')
              doc.setTextColor(80)
              const cLines = doc.splitTextToSize(blotComment.trim(), pageWidth - margin - 110 - 10)
              doc.text(cLines[0] ?? '', margin + 110, y)
              doc.setTextColor(0)
              doc.setFont('helvetica', 'normal')
              doc.setFontSize(9)
            }
            y += 5
          })
          y += 7
        }
      }

      // Case-level comment — only if enabled and non-empty
      if (pdfOptions.includeComments && caseComment?.trim()) {
        checkNewPage(40)
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('Commentaires de l\'opérateur', margin, y)
        y += 8
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        const commentLines = doc.splitTextToSize(caseComment.trim(), pageWidth - margin * 2)
        commentLines.forEach((line: string) => {
          checkNewPage(8)
          doc.text(line, margin, y)
          y += 5
        })
        y += 7
      }

      // Limitations
      if (pdfContent.limitations.length > 0) {
        checkNewPage(40)

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('Limitations', margin, y)
        y += 7

        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        pdfContent.limitations.forEach((limitation) => {
          const lines = doc.splitTextToSize(`• ${limitation}`, pageWidth - margin * 2)
          lines.forEach((line: string) => {
            checkNewPage(10)
            doc.text(line, margin, y)
            y += 4
          })
        })
        y += 7
      }

      // Footer on each page — redraw brand band on every page except page 1
      // (page 1 already has it from the initial drawBrandBand() call).
      const totalPages = doc.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        if (i > 1) drawBrandBand()

        // Compute disclaimer line count so the accent line + text block
        // stack vertically without overlapping.
        doc.setFontSize(8)
        const disclaimerLines = doc.splitTextToSize(
          pdfContent.footer.disclaimer,
          pageWidth - margin * 2,
        ) as string[]
        const lineHeight = 3.5
        const versionLineGap = 4
        const versionY = pageHeight - 8
        // Disclaimer block ends just above the version line.
        const disclaimerBottomY = versionY - versionLineGap
        const disclaimerTopY = disclaimerBottomY - (disclaimerLines.length - 1) * lineHeight
        // Accent line above the whole footer block.
        const accentY = disclaimerTopY - 4
        doc.setFillColor(13, 148, 136)
        doc.rect(margin, accentY, pageWidth - margin * 2, 0.3, 'F')

        doc.setTextColor(100)
        disclaimerLines.forEach((line, idx) => {
          doc.text(line, pageWidth / 2, disclaimerTopY + idx * lineHeight, { align: 'center' })
        })
        doc.text(
          `Careflai · ${pdfContent.footer.version} · Page ${i}/${totalPages}`,
          pageWidth / 2,
          versionY,
          { align: 'center' },
        )
        doc.setTextColor(0)
      }

      // Filename: realization date + source filename (without ext) + transfer
      // lot — sanitised so only filesystem-safe characters survive.
      const sanitise = (s: string) =>
        s.normalize('NFD').replace(/[̀-ͯ]/g, '')  // strip accents
          .replace(/[^a-zA-Z0-9-_]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 80)
      const sourceFileBase = (pdfContent.identification.filename || '')
        .replace(/\.[^.]+$/, '')  // drop extension
      const parts = [
        'rapport-blot',
        pdfOptions.realizationDate,
        sanitise(sourceFileBase),
        sanitise(pdfOptions.transferLotNumber),
      ].filter(p => p && p.length > 0)
      const pdfFileName = `${parts.join('_')}.pdf`
      doc.save(pdfFileName)

      toast({
        title: t('blotAnalysis.export.success', 'Export réussi'),
        description: t('blotAnalysis.export.pdfDownloaded', 'Rapport PDF téléchargé'),
        status: 'success',
        duration: 3000,
      })
      handlePdfClose()
    } catch (error) {
      toast({
        title: t('blotAnalysis.export.error', 'Erreur d\'export'),
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setExporting(null)
    }
  }

  return (
    <>
      <Menu placement="left-end" gutter={12} closeOnSelect>
        <MenuButton
          as={Button}
          variant="ghost"
          isDisabled={isDisabled || !!exporting}
          aria-label={t('blotAnalysis.export.button', 'Exporter')}
          sx={{
            // Three pulses on unlock, then a discreet halo that keeps the button
            // reading as "the next thing to do" without flashing.
            animation: justUnlocked ? `${exportReadyPulse} 1.45s ease-in-out 3` : undefined,
            filter: !isDisabled && !justUnlocked
              ? 'drop-shadow(0 0 5px rgba(168, 85, 247, 0.30))'
              : undefined,
            // Someone who asked for less motion gets the halo, not the pulse.
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              filter: !isDisabled ? 'drop-shadow(0 0 5px rgba(168, 85, 247, 0.30))' : undefined,
            },
          }}
        >
          {exporting ? <Spinner size="sm" /> : <Icon as={LuDownload} boxSize={5} />}
        </MenuButton>
        <Portal>
          <MenuList
            p={3}
            borderRadius="2xl"
            boxShadow="0 20px 60px rgba(15,23,42,0.18), 0 6px 18px rgba(15,118,110,0.10)"
            border="1px solid"
            borderColor="gray.100"
            minW="280px"
            bg="white"
            zIndex={1500}
          >
            <Box px={2} pb={2} mb={1}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.400" letterSpacing="0.08em" textTransform="uppercase">
                {t('blotAnalysis.export.button', 'Exporter')}
              </Text>
            </Box>

            {!allStripIdsComplete && (
              <Box mx={2} mb={2} p={2.5} borderRadius="xl" bg="orange.50" border="1px solid" borderColor="orange.200">
                <Text fontSize="xs" fontWeight="700" color="orange.700" mb={0.5}>
                  {t('blotAnalysis.export.missingIdsTitle', 'Identifiants incomplets')}
                </Text>
                <Text fontSize="xs" color="orange.600" lineHeight="1.4">
                  {t('blotAnalysis.export.missingIdsInline', {
                    defaultValue: 'Complétez l\'identifiant avant export : {{list}}',
                    list: missingStripLabels.join(', '),
                  })}
                </Text>
              </Box>
            )}

            {[
              { id: 'pdf',  icon: LuFileText,        label: t('blotAnalysis.export.pdf', 'Rapport PDF'),       sub: 'Format imprimable pour validation médicale', bg: 'teal.50',   iconBg: 'teal.100',   iconColor: 'teal.600',   badge: { text: 'Imprimable', scheme: 'green' },  onClick: onPdfOpen,        disabled: exporting === 'pdf', divider: true },
              { id: 'json', icon: LuFileJson,        label: t('blotAnalysis.export.json', 'JSON (Données)'),   sub: 'Données structurées pour intégration dev',   bg: 'blue.50',   iconBg: 'blue.100',   iconColor: 'blue.600',   badge: { text: 'Dev', scheme: 'blue' },          onClick: handleExportJSON, disabled: exporting === 'json' },
              { id: 'csv',  icon: LuFileSpreadsheet, label: t('blotAnalysis.export.csv', 'CSV (Excel)'),       sub: 'Tableau de statistiques exploitables',       bg: 'purple.50', iconBg: 'purple.100', iconColor: 'purple.600', badge: { text: 'Stats', scheme: 'purple' },      onClick: handleExportCSV,  disabled: exporting === 'csv', divider: true },
              { id: 'fhir', icon: LuHospital,        label: t('blotAnalysis.export.fhir', 'FHIR (Interop CHU)'), sub: 'Bundle HL7 pour DPI / LIMS',               bg: 'orange.50', iconBg: 'orange.100', iconColor: 'orange.600', badge: { text: 'HL7', scheme: 'orange' },        onClick: onFhirOpen,       disabled: false },
            ].map((item, idx, arr) => (
              <Box key={item.id}>
                <MenuItem
                  onClick={item.onClick}
                  isDisabled={item.disabled || !allStripIdsComplete}
                  borderRadius="xl"
                  px={2.5}
                  py={2.5}
                  mb={1}
                  _last={{ mb: 0 }}
                  _hover={{ bg: item.bg }}
                  transition="all 0.15s"
                >
                  <HStack spacing={3} align="center" w="full">
                    <Box w="38px" h="38px" borderRadius="lg" bg={item.iconBg} display="grid" sx={{ placeItems: 'center' }} flexShrink={0}>
                      <Icon as={item.icon} boxSize={4} color={item.iconColor} />
                    </Box>
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.2">{item.label}</Text>
                      <Text fontSize="xs" color="gray.400" lineHeight="1.35" noOfLines={1}>{item.sub}</Text>
                    </Box>
                    <Badge colorScheme={item.badge.scheme} fontSize="2xs" borderRadius="full" px={2} flexShrink={0}>
                      {item.badge.text}
                    </Badge>
                  </HStack>
                </MenuItem>
                {item.divider && idx < arr.length - 1 && <Box h="1px" mx={2} my={1} bg="gray.100" />}
              </Box>
            ))}
          </MenuList>
        </Portal>
      </Menu>

      {/* PDF Export Modal */}
      <Modal isOpen={isPdfOpen} onClose={handlePdfClose} isCentered size="2xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" overflow="hidden">
          <ModalHeader
            bgGradient="linear(135deg, teal.500, cyan.500)"
            color="white"
            py={5}
          >
            <HStack spacing={3}>
              <Icon as={LuFileText} boxSize={5} />
              <Text>{t('blotAnalysis.export.pdfTitle', 'Export Rapport PDF')}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={4} />

          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <Text fontSize="sm" color="gray.500">
                {t(
                  'blotAnalysis.export.pdfDescription',
                  'Générez un rapport PDF imprimable avec les résultats d\'analyse.'
                )}
              </Text>

              {/* ── Section: Traçabilité (2 columns) ──────────────────── */}
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="teal.600" textTransform="uppercase" letterSpacing="wider" mb={3}>
                  {t('blotAnalysis.export.traceability', 'Traçabilité')}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired isInvalid={isLotNumberInvalid}>
                    <FormLabel fontSize="sm">{t('blotAnalysis.export.lotNumber', 'N° de lot réactifs')}</FormLabel>
                    <Input
                      placeholder="ex: LOT-2024-001"
                      value={effectiveLotNumber}
                      onChange={(e) =>
                        setPdfOptions({ ...pdfOptions, lotNumber: e.target.value })
                      }
                      onBlur={() => setLotNumberTouched(true)}
                      borderRadius="lg"
                      focusBorderColor="teal.400"
                    />
                    {isLotNumberInvalid && (
                      <FormErrorMessage>
                        {t('blotAnalysis.export.lotNumberRequired', 'Le numéro de lot est obligatoire')}
                      </FormErrorMessage>
                    )}
                    {/* Say where the value came from, or why it could not be
                        filled — a pre-filled field with no provenance is a
                        value the reader has no way to challenge. */}
                    {!isLotNumberInvalid && lotFromPlate && !pdfOptions.lotNumber.trim() && (
                      <FormHelperText fontSize="xs" color="teal.600">
                        {t('blotAnalysis.export.lotFromPlate')}
                      </FormHelperText>
                    )}
                    {!isLotNumberInvalid && !lotFromPlate && scopedLots.length > 1 && (
                      <FormHelperText fontSize="xs" color="orange.600">
                        {t('blotAnalysis.export.lotMixed', { lots: scopedLots.join(', ') })}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={isTransferLotNumberInvalid}>
                    <FormLabel fontSize="sm">
                      {t('blotAnalysis.export.transferLotNumber', 'N° de lot de transfert')}
                    </FormLabel>
                    <Input
                      placeholder="ex: TRANSFER-2024-001"
                      value={pdfOptions.transferLotNumber}
                      onChange={(e) =>
                        setPdfOptions({ ...pdfOptions, transferLotNumber: e.target.value })
                      }
                      onBlur={() => setTransferLotNumberTouched(true)}
                      borderRadius="lg"
                      focusBorderColor="teal.400"
                    />
                    {isTransferLotNumberInvalid && (
                      <FormErrorMessage>
                        {t('blotAnalysis.export.transferLotNumberRequired', 'Le numéro de lot de transfert est obligatoire')}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">
                      {t('blotAnalysis.export.realizationDate', 'Date de réalisation')}
                    </FormLabel>
                    <Input
                      type="date"
                      value={pdfOptions.realizationDate}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) =>
                        setPdfOptions({ ...pdfOptions, realizationDate: e.target.value })
                      }
                      borderRadius="lg"
                      focusBorderColor="teal.400"
                    />
                    <FormHelperText fontSize="xs">
                      {t('blotAnalysis.export.realizationDateHelp', 'Différente de la date d\'export')}
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">
                      {t('blotAnalysis.export.organization', 'Organisation')}
                    </FormLabel>
                    <Input
                      placeholder="ex: CHU Bordeaux"
                      value={pdfOptions.organizationName}
                      onChange={(e) =>
                        setPdfOptions({ ...pdfOptions, organizationName: e.target.value })
                      }
                      borderRadius="lg"
                      focusBorderColor="teal.400"
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* ── Section: Contenu du rapport (2 columns of switches) ── */}
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="teal.600" textTransform="uppercase" letterSpacing="wider" mb={3}>
                  Contenu du rapport
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={3}>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between" bg="gray.50" px={3} py={2} borderRadius="lg">
                    <HStack spacing={2}>
                      <Icon as={LuImage} color="teal.500" boxSize={4} />
                      <FormLabel mb="0" fontSize="sm" fontWeight="medium">
                        {t('chu.export.includeRawImages', 'Images brutes')}
                      </FormLabel>
                    </HStack>
                    <Switch
                      colorScheme="teal"
                      size="sm"
                      isChecked={pdfOptions.includeBlotImages}
                      onChange={(e) =>
                        setPdfOptions({ ...pdfOptions, includeBlotImages: e.target.checked })
                      }
                    />
                  </FormControl>

                  {chuFeatures.export.include_operator && (
                    <FormControl display="flex" alignItems="center" justifyContent="space-between" bg="gray.50" px={3} py={2} borderRadius="lg">
                      <FormLabel mb="0" fontSize="sm" fontWeight="medium">
                        {t('chu.export.includeOperator', 'Opérateur')}
                      </FormLabel>
                      <Switch
                        colorScheme="teal"
                        size="sm"
                        isChecked={pdfOptions.includeOperator}
                        onChange={(e) =>
                          setPdfOptions({ ...pdfOptions, includeOperator: e.target.checked })
                        }
                      />
                    </FormControl>
                  )}

                  {chuFeatures.export.include_comments && (
                    <FormControl display="flex" alignItems="center" justifyContent="space-between" bg="gray.50" px={3} py={2} borderRadius="lg">
                      <FormLabel mb="0" fontSize="sm" fontWeight="medium">
                        {t('chu.export.includeComments', 'Commentaires')}
                      </FormLabel>
                      <Switch
                        colorScheme="teal"
                        size="sm"
                        isChecked={pdfOptions.includeComments}
                        onChange={(e) =>
                          setPdfOptions({ ...pdfOptions, includeComments: e.target.checked })
                        }
                      />
                    </FormControl>
                  )}
                </SimpleGrid>
                <Text fontSize="xs" color="gray.500" mt={3}>
                  {pdfOptions.includeBlotImages
                    ? 'L’image source brute et les images des blots seront incluses (fichier plus volumineux).'
                    : 'Seul le tableau récapitulatif sera inclus (fichier léger).'}
                </Text>
              </Box>

              {/* Optional comment — moved here from the workspace page so the
                  operator can add it right before exporting. Hidden when the
                  "Commentaires" switch is off: the comment would not be printed,
                  so offering the field invited writing something that silently
                  never reaches the report. */}
              {chuFeatures.comments.enabled && pdfOptions.includeComments && (
                <Box
                  borderWidth="1px"
                  borderColor="purple.100"
                  bg="purple.50"
                  borderRadius="lg"
                  p={4}
                >
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                      {t('blotAnalysis.export.commentTitle', 'Ajouter un commentaire')}
                    </Text>
                    <Badge
                      colorScheme={(caseComment ?? '').trim() ? 'purple' : 'gray'}
                      variant="subtle"
                      borderRadius="full"
                      px={2}
                    >
                      {(caseComment ?? '').trim()
                        ? t('blotAnalysis.export.commentWillBeIncluded', 'Sera inclus')
                        : t('blotAnalysis.export.commentOptional', 'Optionnel')}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    {t('blotAnalysis.export.commentHelp', 'Contexte clinique, note opérateur ou réserve à joindre au rapport.')}
                  </Text>
                  <Textarea
                    placeholder={t('blotAnalysis.export.commentPlaceholder', 'Observations cliniques, notes de validation, réserves…')}
                    value={caseComment ?? ''}
                    onChange={(e) => onCaseCommentChange?.(e.target.value)}
                    rows={3}
                    resize="vertical"
                    minH="90px"
                    fontSize="sm"
                    bg="white"
                    borderColor="purple.200"
                    _hover={{ borderColor: 'purple.300' }}
                    _focus={{
                      borderColor: 'purple.400',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)',
                    }}
                  />
                  <Text fontSize="2xs" color="gray.500" mt={1} textAlign="right">
                    {t('blotAnalysis.export.commentCharCount', { count: (caseComment ?? '').length })}
                  </Text>
                </Box>
              )}

              <Badge
                bgGradient="linear(135deg, teal.500, cyan.500)"
                color="white"
                p={2}
                borderRadius="lg"
                w="full"
                textAlign="center"
                fontSize="xs"
                fontWeight="semibold"
              >
                {pathologyDisplayName(pathologyName) || 'Analyse Western Blot'}
              </Badge>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
            <Button variant="ghost" onClick={handlePdfClose} size="sm">
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              bgGradient="linear(135deg, teal.500, cyan.500)"
              color="white"
              _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              onClick={() => {
                setLotNumberTouched(true)
                setTransferLotNumberTouched(true)
                if (canExportPDF) {
                  handleExportPDF()
                }
              }}
              isLoading={exporting === 'pdf'}
              leftIcon={<Icon as={LuDownload} />}
              isDisabled={!canExportPDF}
              size="sm"
            >
              {t('blotAnalysis.export.downloadPDF', 'Télécharger PDF')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* FHIR Export Modal */}
      <Modal isOpen={isFhirOpen} onClose={onFhirClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" overflow="hidden">
          <ModalHeader
            bgGradient="linear(135deg, teal.500, cyan.500)"
            color="white"
            py={5}
          >
            <HStack spacing={3}>
              <Icon as={LuHospital} boxSize={5} />
              <Text>{t('blotAnalysis.export.fhirTitle', 'Export FHIR DiagnosticReport')}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={4} />

          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.500">
                {t(
                  'blotAnalysis.export.fhirDescription',
                  'Export au format HL7 FHIR R4 pour intégration avec les systèmes d\'information hospitaliers (SIL, DPI).'
                )}
              </Text>

              <FormControl>
                <FormLabel fontSize="sm">
                  {t('blotAnalysis.export.organization', 'Organisation')}
                </FormLabel>
                <Input
                  placeholder="ex: CHU Bordeaux"
                  value={fhirOptions.organizationName}
                  onChange={(e) =>
                    setFhirOptions({ ...fhirOptions, organizationName: e.target.value })
                  }
                  borderRadius="lg"
                  focusBorderColor="teal.400"
                />
              </FormControl>

              <Badge
                bgGradient="linear(135deg, teal.500, cyan.500)"
                color="white"
                p={2}
                borderRadius="lg"
                w="full"
                textAlign="center"
                fontSize="xs"
                fontWeight="semibold"
              >
                {pathologyDisplayName(pathologyName) || 'Analyse Western Blot'}
              </Badge>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
            <Button variant="ghost" onClick={onFhirClose} size="sm">
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              bgGradient="linear(135deg, teal.500, cyan.500)"
              color="white"
              _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.600)' }}
              onClick={handleExportFHIR}
              isLoading={exporting === 'fhir'}
              leftIcon={<Icon as={LuDownload} />}
              size="sm"
            >
              {t('blotAnalysis.export.downloadFHIR', 'Télécharger FHIR')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
